import { createFifoCache } from '@ardoq/common-helpers';
import { getFillAndStroke } from '@ardoq/color-helpers';
import { colors } from '@ardoq/design-tokens';
import { ArdoqIconCategory, IconName } from '@ardoq/icons';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import { ZoomTransform } from 'd3';
import { darken } from 'polished';
import {
  FALLBACK_IMAGE,
  canvasResolvedImages,
  svgImage,
} from 'tabview/canvasRendering/canvasResolvedImages';
import type { Point } from '@ardoq/math';
import {
  EXPANDED_GROUP_TEXT_NOMINALSIZE,
  EXPANDER_RADIUS,
  SUBLABEL_NOMINALSIZE,
  TEXT_NOMINALSIZE,
} from '../consts';
import { RelationshipsNode } from '../types';
import { canvasFontAwesomeIcon } from 'tabview/canvasRendering/canvasFontAwesomeIcon';
import { nodeLabelY } from './nodeLabelY';
import { AREOLA_GAP } from './consts';
import { shouldShowExpander, markFatness, badgeLocation } from './util';
import { drawDescendantCountBadge } from 'tabview/canvasRendering/descendantCountBadge/drawDescendantCountBadge';
import { groupDisconnectedChildren } from '../viewModel$/groupDisconnectedChildren';
import { MINIMUM_READABLE_FONT_SIZE } from 'tabview/consts';
import { drawNodeImage } from './drawNodeImage';
import { drawSpriteIcon } from 'tabview/blocks/visual/node/drawSpriteIcon';

export const absolutePosition = (node: RelationshipsNode): Point => {
  const { x, y } = node;
  if (node.parent) {
    const [parentX, parentY] = absolutePosition(node.parent);
    return [x + parentX, y + parentY];
  }
  return [x, y];
};
const DESCENDANT_COUNT_BADGE_FONT_SIZE = 12;

// tint(0.8)(grey200) ≈ grey50, matching the old DEFAULT_COLORS.fill for uncoloured nodes
const DEFAULT_RAW_COLOR = colors.grey200;

const resolveComponentColors = (
  node: RelationshipsNode,
  isHighlighted: boolean
) => {
  const { fill: bgFill, stroke: bgStroke } = getFillAndStroke(
    node.color.fill ?? DEFAULT_RAW_COLOR,
    true
  );
  const actualFill = isHighlighted ? highlightGroupFill(bgFill) : bgFill;
  return { fillStyle: actualFill, strokeStyle: bgStroke };
};

const addArcs = (
  context: CanvasRenderingContext2D,
  nodeX: number,
  nodeY: number,
  nodeRadius: number,
  expanderRadius: number,
  viewTransform: ZoomTransform
) => {
  const areolaRadius = expanderRadius + AREOLA_GAP * viewTransform.k;
  const upperAngle = 2 * Math.asin(areolaRadius / (2 * nodeRadius));
  const lowerAngle = (Math.PI - upperAngle) / 2;

  context.arc(
    nodeX,
    nodeY,
    nodeRadius,
    Math.PI / 2 + upperAngle,
    Math.PI / 2 - upperAngle,
    false
  ); // upper arc
  context.arc(
    nodeX,
    nodeY + nodeRadius,
    areolaRadius,
    -Math.PI / 2 + lowerAngle,
    -Math.PI / 2 - lowerAngle,
    false
  ); // lower arc
};

const adjustFontSize = (
  nominalSize: number,
  viewTransform: ZoomTransform,
  scale: number
) => nominalSize * viewTransform.k * devicePixelRatio * Math.min(scale, 1);

export const getNodeFontSize = (
  node: RelationshipsNode,
  viewTransform: ZoomTransform,
  scale: number
) => adjustFontSize(getNominalFontSize(node), viewTransform, scale);

const getNominalFontSize = ({ descendantCount, open }: RelationshipsNode) =>
  descendantCount && open ? EXPANDED_GROUP_TEXT_NOMINALSIZE : TEXT_NOMINALSIZE;

export const getSubLabelHeight = (
  viewTransform: ZoomTransform,
  scale: number
) => adjustFontSize(SUBLABEL_NOMINALSIZE, viewTransform, scale);

const drawNodeRepresentation = (
  node: RelationshipsNode,
  context: CanvasRenderingContext2D,
  iconRadius: number,
  x: number,
  y: number
) => {
  if (!node.representationData) {
    return;
  }
  const { isImage, value, icon } = node.representationData;
  if (isImage && value) {
    node.isBrokenImage = canvasResolvedImages.get(value) === FALLBACK_IMAGE;
    drawNodeImage(context, node, value, x, y, iconRadius);
  } else if (icon) {
    const iconColor = node.color.fill ?? colors.black950;
    if (icon.category === ArdoqIconCategory.FontAwesome) {
      canvasFontAwesomeIcon(context, x, y, icon.id, iconRadius, iconColor);
    } else if (icon.isSVG) {
      context.drawImage(
        // args interpolation: ${iconId}~~${color}~~${fill}~~${stroke}~~${width}~~${height}
        svgImage(`${icon.id}~~${iconColor}`),
        x - iconRadius / 2,
        y - iconRadius / 2,
        iconRadius,
        iconRadius
      );
    }
  }
};

const drawExpanderButton = (
  x: number,
  y: number,
  r: number,
  open: boolean,
  expanderRadius: number,
  context: CanvasRenderingContext2D
) => {
  context.beginPath();
  context.fillStyle = colors.white950;
  context.strokeStyle = colors.grey300;
  context.lineWidth = 2;
  context.ellipse(x, y + r, expanderRadius, expanderRadius, 0, 0, 2 * Math.PI);
  context.fill();
  context.stroke();

  drawSpriteIcon(
    open ? IconName.UNFOLD_LESS : IconName.UNFOLD_MORE,
    expanderRadius * 1.5,
    colors.grey500,
    x,
    y + r,
    context
  );
};

export const getChildCountBadgeFontSizeForNode = (
  scaledNodeR: number,
  viewTransform: ZoomTransform,
  scale: number
) =>
  Math.round(
    Math.max(
      scaledNodeR / 5,
      DESCENDANT_COUNT_BADGE_FONT_SIZE *
        viewTransform.k *
        devicePixelRatio *
        Math.min(scale, 1)
    )
  );

export const isChildCountBadgeToBeDisplayed = (
  childCountBadgeFontSize: number
) => childCountBadgeFontSize / devicePixelRatio > 6;

const highlightGroupFill = createFifoCache(Infinity, darken(0.03));

/** @returns true if the font size is big enough to read. */
export const shouldShowNodeLabel = (fontSize: number) =>
  fontSize / devicePixelRatio > MINIMUM_READABLE_FONT_SIZE;

const drawNodeHighlight = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  gap: number
) => {
  context.beginPath();
  context.ellipse(x, y, r + gap, r + gap, 0, 0, 2 * Math.PI);
  context.stroke();
};
const shouldShowIcon = ({
  representationData,
  descendantCount,
  open,
  r,
}: RelationshipsNode) =>
  representationData && (!descendantCount || !open) && r / devicePixelRatio > 1;

export const drawNode = (
  node: RelationshipsNode,
  label: string,
  subLabel: string | null,
  viewTransform: ZoomTransform,
  context: CanvasRenderingContext2D,
  windowSize: [width: number, height: number],
  scale: number,
  marked: boolean,
  highlighted: boolean,
  disconnectedNodesHighlighted: boolean
) => {
  const {
    x: nodeX,
    y: nodeY,
    r: nodeR,
    isSynthetic,
    open,
    descendantCount,
    isContext,
  } = node;
  if (isSynthetic) {
    return;
  }
  const [x, y] = viewTransform.apply([nodeX, nodeY]);
  const r = nodeR * viewTransform.k;
  if (
    x + r < 0 ||
    y + r < 0 ||
    x - r > windowSize[0] ||
    y - r > windowSize[1]
  ) {
    // node is off-screen, we don't need to draw it
    return;
  }

  context.beginPath();
  const { fillStyle, strokeStyle } = resolveComponentColors(node, highlighted);
  context.strokeStyle = strokeStyle;
  context.fillStyle = fillStyle;

  const fontSize = getNodeFontSize(node, viewTransform, scale);
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  const showExpander = shouldShowExpander(node, viewTransform.k);
  const expanderRadius = showExpander ? EXPANDER_RADIUS * viewTransform.k : 0;
  const fatness = isContext || marked ? markFatness(r) : 1;
  const markGap = fatness * 1.5;
  if (showExpander) {
    context.lineWidth = fatness;
    addArcs(context, x, y, r, expanderRadius, viewTransform);
    context.fill();
    context.strokeStyle = isContext ? colors.blue400 : context.strokeStyle;
    context.stroke();
  } else {
    context.lineWidth = 1;
    context.ellipse(x, y, r, r, 0, 0, 2 * Math.PI); // main circle
    context.fill();
    context.stroke();

    // #region context/highlight mark
    context.lineWidth = fatness;

    if (isContext) {
      context.lineWidth = fatness;
      context.strokeStyle = colors.blue400;
      drawNodeHighlight(context, x, y, r, markGap);
    } else if (marked) {
      drawNodeHighlight(context, x, y, r, markGap);
    }
    // #endregion
  }

  const showIcon = shouldShowIcon(node);

  // #region closed group icon
  if (!open && showIcon && descendantCount) {
    context.lineWidth = 1;
    context.beginPath();
    context.ellipse(x, y, r / 2, r / 2, 0, 0, 2 * Math.PI);
    context.fill();
    context.strokeStyle = strokeStyle;
    context.stroke();
  }
  // #endregion
  if (showExpander) {
    drawExpanderButton(x, y, r, open, expanderRadius, context);
  }

  // #region node label
  if (shouldShowNodeLabel(fontSize)) {
    const nodeLabelFont = `${fontSize}px ${ARDOQ_DEFAULT_FONT_FAMILY}`;
    context.font = nodeLabelFont;
    const showSubLabel = subLabel && node.open;
    const subLabelHeight = showSubLabel
      ? getSubLabelHeight(viewTransform, scale)
      : 0;

    const labelY =
      viewTransform.applyY(
        nodeLabelY(node, showExpander, marked || isContext)
      ) +
      fontSize / 2; // add half the line height because the label will be middle-aligned.
    const subLabelY = labelY + fontSize;

    const isOpenGroup = showExpander && open;
    const labelWidth = isOpenGroup ? context.measureText(label).width : 0; // no need to measure width when not showing icon
    const labelX = isOpenGroup ? x + fontSize : x;

    if (isOpenGroup) {
      drawNodeRepresentation(
        node,
        context,
        node.representationData?.isImage ? fontSize / 2 : fontSize,
        labelX - labelWidth / 2 - fontSize * 0.75,
        labelY
      );
      context.font = nodeLabelFont; // drawNodeRepresentation will have changed the font if the icon is from FontAwesome. we must set it back before we draw the node label.
    }
    context.fillStyle = colors.black950;
    context.fillText(label, labelX, labelY);
    if (showSubLabel) {
      context.font = `italic ${subLabelHeight}px ${ARDOQ_DEFAULT_FONT_FAMILY}`;
      context.fillText(subLabel, x, subLabelY);
    }
  }
  // #endregion

  if (!open && descendantCount) {
    const descendantCountBadgeFontSize = getChildCountBadgeFontSizeForNode(
      r,
      viewTransform,
      scale
    );
    if (isChildCountBadgeToBeDisplayed(descendantCountBadgeFontSize)) {
      const disconnectedChildrenCount = groupDisconnectedChildren(
        [],
        node
      ).length;
      const [badgeX, badgeY] = badgeLocation(x, y, r);
      drawDescendantCountBadge({
        fontSize: descendantCountBadgeFontSize,
        x: badgeX,
        y: badgeY,
        descendantCount,
        context,
        disconnectedNodesHighlighted,
        disconnectedChildrenCount,
        background: colors.grey700,
      });
    }
  }

  if (!showIcon) {
    return;
  }

  const iconRadius = descendantCount ? r / 2 : r;

  drawNodeRepresentation(node, context, iconRadius, x, y);
};
